import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ComposedChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AdminGate } from "@/components/admin/AdminGate";
import { PeriodFilter, usePeriodFilter } from "@/components/admin/PeriodFilter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCrmDiagnostico, syncCrmDeals } from "@/lib/crm/diagnostico.functions";

export const Route = createFileRoute("/admin/diagnostico-comercial")({
  head: () => ({
    meta: [
      { title: "Diagnóstico Comercial — Admin" },
      { name: "description", content: "Onde o funil comercial vaza entre lead e venda." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/diagnostico-comercial">
      <DiagnosticoPage />
    </AdminGate>
  ),
});

const ACCENT = "#f5b022";
const TEAL = "#3fd5c9";
const BLUE = "#3b82f6";
const RED = "#ef4444";
const GREY = "#64748b";

const num = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("pt-BR"));
const pct = (n: number | null | undefined) =>
  n == null ? "—" : `${n.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const money = (n: number | null | undefined) =>
  n == null
    ? "—"
    : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const fmtDate = (s: string | null | undefined) =>
  !s ? "—" : new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });

const chartTheme = {
  grid: "rgba(255,255,255,0.08)",
  axis: "rgba(255,255,255,0.45)",
};

function Card({
  title,
  hint,
  children,
  className = "",
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-xl border border-white/10 bg-white/5 p-4 ${className}`}>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-white/80">{title}</h3>
      {hint && <p className="mt-1 text-[11px] leading-relaxed text-white/40">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-[11px] uppercase tracking-wide text-white/50">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-white">{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-white/40">{hint}</div>}
    </div>
  );
}

function tooltipStyle() {
  return {
    contentStyle: {
      background: "#0b1220",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: 8,
      fontSize: 12,
      color: "#fff",
    },
    labelStyle: { color: "rgba(255,255,255,0.6)" },
  };
}

function DiagnosticoPage() {
  const qc = useQueryClient();
  const period = usePeriodFilter("month");
  const [pipeline, setPipeline] = useState("all");
  const [seller, setSeller] = useState("all");
  const [temperature, setTemperature] = useState("all");
  const [source, setSource] = useState("all");

  const fromIso = period.range.from ?? new Date(Date.now() - 90 * 86_400_000).toISOString();
  const toIso = period.range.to ?? new Date().toISOString();

  const fetchFn = useServerFn(getCrmDiagnostico);
  const q = useQuery({
    queryKey: ["crm-diag", fromIso, toIso, pipeline, seller, temperature, source],
    queryFn: () =>
      fetchFn({
        data: {
          fromIso,
          toIso,
          pipeline: pipeline === "all" ? undefined : pipeline,
          seller: seller === "all" ? undefined : seller,
          temperature: temperature === "all" ? undefined : temperature,
          source: source === "all" ? undefined : source,
        },
      }),
  });

  const syncFn = useServerFn(syncCrmDeals);
  const sync = useMutation({
    mutationFn: (days: number) => syncFn({ data: { days } }),
    onSuccess: (r) => {
      toast.success(
        `Sincronizado: ${r.fetched} negociações lidas, ${r.upserted} atualizadas${r.truncated ? " (parcial)" : ""}.`,
      );
      qc.invalidateQueries({ queryKey: ["crm-diag"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Falha na sincronização"),
  });

  const d = q.data;

  const stageChart = useMemo(
    () =>
      (d?.stages ?? []).map((s) => ({
        name: `${s.stage}`,
        pipeline: s.pipeline,
        Abertas: s.open,
        Ganhas: s.won,
        Perdidas: s.lost,
      })),
    [d],
  );

  const outcomePie = useMemo(
    () =>
      d
        ? [
            { name: "Ganhas", value: d.totals.won, fill: TEAL },
            { name: "Perdidas", value: d.totals.lost, fill: RED },
            { name: "Abertas", value: d.totals.open, fill: GREY },
          ].filter((x) => x.value > 0)
        : [],
    [d],
  );

  return (
    <div className="min-h-screen px-4 py-8 text-white sm:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Link to="/admin/inteligencia-leads" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white">
              <ArrowLeft className="h-3.5 w-3.5" /> Voltar para Inteligência Leads/CRM
            </Link>

            <h1 className="mt-2 text-2xl font-semibold">Diagnóstico Comercial</h1>
            <p className="mt-1 max-w-2xl text-sm text-white/50">
              Cruza as negociações do RD Station CRM com a captura de leads e as vendas pagas para
              mostrar onde o funil perde dinheiro: atendimento, tempo de resposta, qualificação ou
              fechamento.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button
              onClick={() => sync.mutate(90)}
              disabled={sync.isPending}
              className="bg-[#f5b022] text-black hover:bg-[#f5b022]/90"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${sync.isPending ? "animate-spin" : ""}`} />
              {sync.isPending ? "Sincronizando…" : "Sincronizar CRM (90 dias)"}
            </Button>
            <span className="text-[11px] text-white/40">
              Última sincronização: {fmtDate(d?.last_sync?.finished_at ?? d?.last_sync?.started_at)}
              {d?.last_sync?.status ? ` · ${d.last_sync.status}` : ""}
            </span>
          </div>
        </header>

        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <PeriodFilter
            preset={period.preset}
            setPreset={period.setPreset}
            customFrom={period.customFrom}
            setCustomFrom={period.setCustomFrom}
            customTo={period.customTo}
            setCustomTo={period.setCustomTo}
          />

          <div className="mt-3 grid gap-2 sm:grid-cols-4">
            <FilterSelect label="Funil" value={pipeline} onChange={setPipeline} options={d?.options.pipelines ?? []} />
            <FilterSelect label="Vendedora" value={seller} onChange={setSeller} options={d?.options.sellers ?? []} />
            <FilterSelect
              label="Temperatura"
              value={temperature}
              onChange={setTemperature}
              options={(d?.temperature ?? []).map((t) => t.label)}
            />
            <FilterSelect label="Origem" value={source} onChange={setSource} options={d?.options.sources ?? []} />
          </div>
        </div>

        {q.isLoading && <p className="text-sm text-white/50">Carregando diagnóstico…</p>}
        {q.error && (
          <p className="text-sm text-red-400">
            {q.error instanceof Error ? q.error.message : "Erro ao carregar"}
          </p>
        )}

        {d && d.totals.deals === 0 && (
          <div className="rounded-xl border border-[#f5b022]/30 bg-[#f5b022]/10 p-4 text-sm text-admin-accent">
            Nenhuma negociação sincronizada nesse período. Clique em “Sincronizar CRM (90 dias)” para
            importar as negociações do RD Station CRM.
          </div>
        )}

        {d && d.totals.deals > 0 && (
          <>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Kpi label="Negociações" value={num(d.totals.deals)} hint="criadas no período" />
              <Kpi label="Abertas" value={num(d.totals.open)} />
              <Kpi label="Ganhas" value={num(d.totals.won)} />
              <Kpi label="Perdidas" value={num(d.totals.lost)} />
              <Kpi label="Taxa de ganho" value={pct(d.totals.win_rate)} />
              <Kpi label="Ticket médio (CRM)" value={money(d.totals.avg_ticket)} hint={`Receita ${money(d.totals.revenue)}`} />
            </div>

            {d.findings.length > 0 && (
              <Card
                title="Onde está o buraco"
                hint="Leitura automática das métricas do período. Ordem por gravidade."
              >
                <ul className="space-y-3">
                  {d.findings.map((f) => (
                    <li
                      key={f.title}
                      className="rounded-lg border border-white/10 bg-black/20 p-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <AlertTriangle
                          className={`h-4 w-4 ${f.severity === "alta" ? "text-red-400" : f.severity === "media" ? "text-admin-accent" : "text-white/40"}`}
                        />
                        <span className="text-sm font-semibold text-white">{f.title}</span>
                        <Badge
                          variant="outline"
                          className={`border-white/15 text-[10px] uppercase ${f.severity === "alta" ? "text-red-400" : f.severity === "media" ? "text-admin-accent" : "text-white/50"}`}
                        >
                          {f.metric}
                        </Badge>
                      </div>
                      <p className="mt-1.5 text-xs leading-relaxed text-white/60">{f.detail}</p>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <Tabs defaultValue="funil">
              <TabsList className="flex-wrap bg-white/5">
                <TabsTrigger value="funil">Funil</TabsTrigger>
                <TabsTrigger value="atendimento">Atendimento</TabsTrigger>
                <TabsTrigger value="qualidade">Qualidade do lead</TabsTrigger>
                <TabsTrigger value="time">Time</TabsTrigger>
                <TabsTrigger value="perdas">Perdas & vendas</TabsTrigger>
              </TabsList>

              <TabsContent value="funil" className="mt-4 space-y-4">
                <Card
                  title="Etapas do funil"
                  hint="Quantas negociações estão (ou pararam) em cada etapa. Empilhamento numa etapa inicial indica gargalo de contato; numa etapa final, gargalo de fechamento."
                >
                  <div className="h-[420px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stageChart} layout="vertical" margin={{ left: 120 }}>
                        <CartesianGrid stroke={chartTheme.grid} />
                        <XAxis type="number" stroke={chartTheme.axis} fontSize={11} />
                        <YAxis type="category" dataKey="name" stroke={chartTheme.axis} fontSize={11} width={120} />
                        <Tooltip {...tooltipStyle()} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Abertas" stackId="a" fill={GREY} />
                        <Bar dataKey="Ganhas" stackId="a" fill={TEAL} />
                        <Bar dataKey="Perdidas" stackId="a" fill={RED} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Card title="Resultado das negociações" hint="Distribuição ganho / perda / aberto no período.">
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={outcomePie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100}>
                            {outcomePie.map((s) => (
                              <Cell key={s.name} fill={s.fill} />
                            ))}
                          </Pie>
                          <Tooltip {...tooltipStyle()} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  <Card
                    title="Negociações abertas paradas"
                    hint="Dias desde a última atualização da negociação. Barras à direita são dinheiro esquecido no CRM."
                  >
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={d.idle}>
                          <CartesianGrid stroke={chartTheme.grid} />
                          <XAxis dataKey="bucket" stroke={chartTheme.axis} fontSize={11} />
                          <YAxis stroke={chartTheme.axis} fontSize={11} />
                          <Tooltip {...tooltipStyle()} />
                          <Bar dataKey="deals" name="Abertas" fill={ACCENT} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </div>

                <Card title="Top 50 negociações abertas mais paradas" hint="Lista para ação imediata do time.">
                  <div className="max-h-[420px] overflow-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="sticky top-0 bg-[#0b1220] text-white/50">
                        <tr>
                          <th className="p-2">Negociação</th>
                          <th className="p-2">Contato</th>
                          <th className="p-2">Vendedora</th>
                          <th className="p-2">Etapa</th>
                          <th className="p-2 text-right">Parada (dias)</th>
                          <th className="p-2 text-right">Aberta (dias)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.stale_list.map((s) => (
                          <tr key={s.id} className="border-t border-white/5 text-white/70">
                            <td className="p-2">{s.name}</td>
                            <td className="p-2">{s.contact}</td>
                            <td className="p-2">{s.seller}</td>
                            <td className="p-2">
                              {s.stage}
                              <span className="text-white/30"> · {s.pipeline}</span>
                            </td>
                            <td className="p-2 text-right font-semibold text-admin-accent">{s.days_idle}</td>
                            <td className="p-2 text-right">{s.days_open}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="atendimento" className="mt-4 space-y-4">
                <div className="grid gap-3 sm:grid-cols-4">
                  <Kpi
                    label="Sem nenhum movimento"
                    value={num(d.coverage.never_moved)}
                    hint={`${pct((d.coverage.never_moved / d.coverage.total) * 100)} do total`}
                  />
                  <Kpi
                    label="≤ 1 interação"
                    value={num(d.coverage.single_interaction)}
                    hint="segundo contador do CRM"
                  />
                  <Kpi
                    label="Sem atividade registrada"
                    value={num(d.coverage.no_activity_registered)}
                    hint="nenhuma tarefa/atividade no CRM"
                  />
                  <Kpi
                    label="Abertas na 1ª etapa"
                    value={num(d.coverage.open_in_first_stage)}
                    hint="fila de primeiro contato"
                  />
                </div>

                <Card
                  title="Tempo até o primeiro movimento x taxa de ganho"
                  hint="Proxy: intervalo entre a criação da negociação e a primeira atualização/atividade (a API do CRM não expõe o log de contato). Barras = volume, linha = taxa de ganho."
                >
                  <div className="h-[340px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={d.first_move}>
                        <CartesianGrid stroke={chartTheme.grid} />
                        <XAxis dataKey="bucket" stroke={chartTheme.axis} fontSize={11} />
                        <YAxis yAxisId="l" stroke={chartTheme.axis} fontSize={11} />
                        <YAxis yAxisId="r" orientation="right" stroke={TEAL} fontSize={11} unit="%" />
                        <Tooltip {...tooltipStyle()} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar yAxisId="l" dataKey="deals" name="Negociações" fill={BLUE} radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="l" dataKey="won" name="Ganhas" fill={TEAL} radius={[4, 4, 0, 0]} />
                        <Line yAxisId="r" type="monotone" dataKey="win_rate" name="Taxa de ganho %" stroke={ACCENT} strokeWidth={2} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                <Card
                  title="Negociações duplicadas em aberto"
                  hint="Mesma pessoa com mais de uma negociação aberta: divide o lead entre vendedoras e inflaciona o volume."
                >
                  <p className="text-sm text-white/70">
                    {num(d.duplicates.people)} pessoas · {num(d.duplicates.deals)} negociações
                  </p>
                  {d.duplicates.sample.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-white/50">
                      {d.duplicates.sample.map((s) => (
                        <li key={s.contact}>
                          {s.contact} — {s.deals} negociações
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="qualidade" className="mt-4 space-y-4">
                <Card
                  title="Temperatura do lead x conversão"
                  hint="Se quente não converte mais que morno, o problema está na régua de qualificação — não no time."
                >
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={d.temperature}>
                        <CartesianGrid stroke={chartTheme.grid} />
                        <XAxis dataKey="label" stroke={chartTheme.axis} fontSize={11} />
                        <YAxis yAxisId="l" stroke={chartTheme.axis} fontSize={11} />
                        <YAxis yAxisId="r" orientation="right" stroke={TEAL} fontSize={11} unit="%" />
                        <Tooltip {...tooltipStyle()} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar yAxisId="l" dataKey="deals" name="Negociações" fill={BLUE} radius={[4, 4, 0, 0]} />
                        <Line yAxisId="r" type="monotone" dataKey="win_rate" name="Taxa de ganho %" stroke={ACCENT} strokeWidth={2} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                <Card
                  title="Origem da captura x conversão no CRM"
                  hint="Qual funil de captura gera negociação que realmente fecha. Base: vínculo lead → negociação."
                >
                  <div className="h-[420px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={d.sources} layout="vertical" margin={{ left: 140 }}>
                        <CartesianGrid stroke={chartTheme.grid} />
                        <XAxis type="number" stroke={chartTheme.axis} fontSize={11} />
                        <YAxis type="category" dataKey="label" stroke={chartTheme.axis} fontSize={11} width={140} />
                        <Tooltip {...tooltipStyle()} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="deals" name="Negociações" fill={BLUE} radius={[0, 4, 4, 0]} />
                        <Bar dataKey="won" name="Ganhas" fill={TEAL} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="time" className="mt-4 space-y-4">
                <Card
                  title="Vendedoras"
                  hint="Volume recebido, quantas foram efetivamente movidas, taxa de ganho e mediana de tempo até o primeiro movimento."
                >
                  <div className="overflow-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-white/50">
                        <tr>
                          <th className="p-2">Vendedora</th>
                          <th className="p-2 text-right">Recebidas</th>
                          <th className="p-2 text-right">Trabalhadas</th>
                          <th className="p-2 text-right">Abertas</th>
                          <th className="p-2 text-right">Ganhas</th>
                          <th className="p-2 text-right">Perdidas</th>
                          <th className="p-2 text-right">Ganho %</th>
                          <th className="p-2 text-right">1º movimento (h, mediana)</th>
                          <th className="p-2 text-right">Receita CRM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.sellers.map((s) => (
                          <tr key={s.seller} className="border-t border-white/5 text-white/70">
                            <td className="p-2 text-white">{s.seller}</td>
                            <td className="p-2 text-right">{num(s.deals)}</td>
                            <td className="p-2 text-right">
                              {num(s.moved)}
                              <span className="text-white/30"> ({pct((s.moved / Math.max(s.deals, 1)) * 100)})</span>
                            </td>
                            <td className="p-2 text-right">{num(s.open)}</td>
                            <td className="p-2 text-right text-[#3fd5c9]">{num(s.won)}</td>
                            <td className="p-2 text-right text-red-400">{num(s.lost)}</td>
                            <td className="p-2 text-right font-semibold">{pct(s.win_rate)}</td>
                            <td className="p-2 text-right">{s.median_first_move_hours ?? "—"}</td>
                            <td className="p-2 text-right">{money(s.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card title="Taxa de ganho por vendedora" hint="Somente comparável entre volumes parecidos.">
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={d.sellers}>
                        <CartesianGrid stroke={chartTheme.grid} />
                        <XAxis dataKey="seller" stroke={chartTheme.axis} fontSize={10} interval={0} angle={-20} height={60} textAnchor="end" />
                        <YAxis yAxisId="l" stroke={chartTheme.axis} fontSize={11} />
                        <YAxis yAxisId="r" orientation="right" stroke={TEAL} fontSize={11} unit="%" />
                        <Tooltip {...tooltipStyle()} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar yAxisId="l" dataKey="deals" name="Recebidas" fill={GREY} radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="l" dataKey="won" name="Ganhas" fill={TEAL} radius={[4, 4, 0, 0]} />
                        <Line yAxisId="r" type="monotone" dataKey="win_rate" name="Ganho %" stroke={ACCENT} strokeWidth={2} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="perdas" className="mt-4 space-y-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <Card
                    title="Motivos de perda"
                    hint={`${num(d.lost_without_reason)} de ${num(d.lost_total)} perdas estão sem motivo preenchido.`}
                  >
                    <div className="h-[360px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={d.lost_reasons} layout="vertical" margin={{ left: 150 }}>
                          <CartesianGrid stroke={chartTheme.grid} />
                          <XAxis type="number" stroke={chartTheme.axis} fontSize={11} />
                          <YAxis type="category" dataKey="reason" stroke={chartTheme.axis} fontSize={11} width={150} />
                          <Tooltip {...tooltipStyle()} />
                          <Bar dataKey="deals" name="Perdas" fill={RED} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  <Card
                    title="CRM x vendas pagas (Eduzz)"
                    hint="Confere se o que o CRM marca como ganho corresponde a pagamento — e se há pagamento em negociação não marcada como ganha."
                  >
                    <dl className="space-y-3 text-sm">
                      <Row label="Ganhas no CRM" value={num(d.cross_sales.won)} />
                      <Row label="Ganhas com venda paga" value={num(d.cross_sales.won_with_paid_sale)} tone="good" />
                      <Row label="Ganhas sem venda paga" value={num(d.cross_sales.won_without_paid_sale)} tone="bad" />
                      <Row label="Pagamento sem ganho no CRM" value={num(d.cross_sales.paid_sale_not_won)} tone="warn" />
                      <Row label="Receita paga conciliada" value={money(d.cross_sales.matched_revenue)} />
                    </dl>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad" | "warn";
}) {
  const color =
    tone === "good" ? "text-[#3fd5c9]" : tone === "bad" ? "text-red-400" : tone === "warn" ? "text-admin-accent" : "text-white";
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-2">
      <dt className="text-white/60">{label}</dt>
      <dd className={`font-semibold ${color}`}>{value}</dd>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wide text-white/40">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="border-white/10 bg-black/30 text-xs text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}
